import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeEuro, Boxes, CreditCard, FileText, Truck, RotateCcw, UserPlus, ShieldCheck, ShoppingCart } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSeo } from "@/lib/seo";
import { OrderContact } from "@/components/OrderContact";
import { ORDER_WHATSAPP_URL } from "@/lib/social";

const PERKS = [
  [BadgeEuro, "Prix professionnels HT", "De 6,99 € à 8,99 € HT la pièce, TVA 20 % détaillée sur chaque document."],
  [Boxes, "Quantités au choix", "De 12 à 180 pièces selon le produit, à commander autant de fois que nécessaire."],
  [Truck, "Livraison offerte", "Mondial Relay ou transporteur, sous 10 à 15 jours, dès 300 € HT d'achat (sinon retrait au dépôt de Chatou)."],
  [FileText, "Devis et factures PDF", "Devis immédiat valable 15 jours, factures téléchargeables dans votre compte."],
  [CreditCard, "Paiement simple", "Carte bancaire, PayPal ou virement."],
  [RotateCcw, "Commande express", "Recommandez votre dernière commande en un clic."],
];

const RANGE = [
  ["u01-extreme-crop.webp", "Sérum Extrême Repouss", "8,99 € HT · 12 à 180 pièces"],
  ["u02-extreme-fins.webp", "Extrême Repouss Cheveux Fins", "8,99 € HT · 12 à 180 pièces"],
  ["u14-shamp-locks.webp", "Gamme Locks", "7,99 € HT · shampooing, huile, spray"],
  ["u24-edge.webp", "Edge Control", "8,99 € HT · 12 ou 24 pièces"],
  ["u18-huile-barbe.webp", "Huiles à barbe", "6,99 € HT · 4 parfums"],
  ["u23-masque-supreme.webp", "Masque Suprême", "7,99 € HT · 12 ou 16 pièces"],
];

const STEPS = [
  [UserPlus, "Créez votre compte pro", "Identité, entreprise, SIRET et adresses en 2 minutes."],
  [ShieldCheck, "Validation du compte", "Nous vérifions votre activité professionnelle puis activons vos prix HT."],
  [ShoppingCart, "Commandez", "Choisissez vos quantités, téléchargez un devis ou payez directement."],
];

const FAQ = [
  ["Qui peut devenir revendeur ?", "Les professionnels : salons de coiffure, coiffeurs indépendants, barbiers, boutiques et magasins de cosmétiques, grossistes et distributeurs. Un compte professionnel validé est nécessaire pour voir les prix HT et commander."],
  ["Quel est le minimum de commande ?", "Il n'y a pas de montant minimum : vous commandez par quantités fixes (12, 16, 20, 24 pièces… selon le produit), dès une seule référence."],
  ["Puis-je tester avant de commander en quantité ?", "Oui : commandez 12 pièces d'une seule référence, ou un pack prêt à revendre sur le site (10 produits pour 100 €, 15 pour 150 €, 30 pour 300 €)."],
  ["Quels sont les délais et frais de livraison ?", "La livraison est offerte pour les professionnels, en point relais Mondial Relay ou par transporteur. Comptez 10 à 15 jours. Minimum 300 € HT d'achat pour être livré, sinon retrait au dépôt Ozentya de Chatou (78)."],
  ["Comment obtenir un devis ?", "Ajoutez vos produits au panier de l'Espace Pro puis cliquez sur « Télécharger le devis » : le PDF est généré immédiatement et reste valable 15 jours."],
  ["À quel prix revendre ?", "Vous fixez librement votre prix de revente. Les prix d'achat HT sont affichés dans l'Espace Pro."],
];

const FR = {
  seo: { title: "Devenir revendeur Ozentya — produits capillaires afro à prix pro", description: "Salons, barbiers, boutiques afro : revendez les soins Ozentya (Extrême Repouss, Locks, Edge Control) à prix HT, dès 12 pièces, livraison offerte, devis et factures PDF.", path: "/devenir-revendeur" },
  kicker: "15 ans d'expérience dans les problématiques d'alopécie", h1: "Devenir revendeur Ozentya", alt: ["English version", "/en/become-a-reseller"], intl: false,
  intro: "Proposez à votre clientèle des soins pensés pour les cheveux afro, crépus et texturés, achetés à prix professionnels et livrés gratuitement.",
  cta: "Ouvrir mon compte pro", ctaTo: "/espace-pro/inscription", catalog: "Voir le catalogue pro", catalogTo: "/espace-pro",
  contactTop: "Commander ou se renseigner (France, DOM-TOM, Europe, Afrique, Royaume-Uni)", contactBottom: "Ou contactez directement Ozentya pour commander",
  h2Perks: "Les avantages pour votre activité", h2Range: "La gamme à revendre", rangeSub: "17 références au catalogue pro : pousse, locks, edges, barbe, shampooings, crèmes et masques.",
  h2Steps: "Ouvrir un compte en 3 étapes", step: "Étape", h2Faq: "Questions fréquentes", final: "Prêt à proposer Ozentya dans votre salon ou votre boutique ?",
  perks: PERKS, range: RANGE, steps: STEPS, faq: FAQ,
};

export const EN = {
  seo: { title: "Become an Ozentya reseller — afro hair care for professionals", description: "Afro hair salons, barbershops, beauty stores and distributors: resell Ozentya hair care (Extrême Repouss, Locks range, Edge Control). Contact us on WhatsApp or by email.", path: "/en/become-a-reseller" },
  kicker: "15 years of experience with alopecia issues", h1: "Become an Ozentya reseller", alt: ["Version française", "/devenir-revendeur"], intl: true,
  intro: "Offer your clients hair care designed for afro, kinky, curly and textured hair — and order directly from the brand.",
  cta: "Contact us on WhatsApp", ctaTo: null, catalog: "Discover the products", catalogTo: "/",
  contactTop: "Prices, quantities and orders (UK, Europe, Africa, Americas)", contactBottom: "Contact Ozentya directly to order",
  h2Perks: "Why work with Ozentya", h2Range: "The range", rangeSub: "17 professional references: hair growth, locs, edges, beard, shampoos, creams and masks.",
  h2Steps: "How to order in 3 steps", step: "Step", h2Faq: "Frequently asked questions", final: "Ready to offer Ozentya in your salon or store?",
  perks: [
    [BadgeEuro, "Professional prices", "From €6.99 to €8.99 excl. VAT per unit — ask us for your price list."],
    [Boxes, "Flexible quantities", "From 12 to 180 units depending on the product, reorder as often as you need."],
    [Truck, "Delivery", "Free delivery in France. For other countries, contact us for shipping conditions."],
    [FileText, "Quotes and invoices", "Written quote and invoice with VAT details for every order."],
    [CreditCard, "Easy payment", "Card, PayPal or bank transfer."],
    [ShieldCheck, "15 years of experience", "A brand built on 15 years of experience with hair loss and alopecia issues."],
  ],
  range: [
    ["u01-extreme-crop.webp", "Extrême Repouss serum", "€8.99 excl. VAT · 12 to 180 units"],
    ["u02-extreme-fins.webp", "Extrême Repouss Fine Hair", "€8.99 excl. VAT · 12 to 180 units"],
    ["u14-shamp-locks.webp", "Locks range", "€7.99 excl. VAT · shampoo, oil, spray"],
    ["u24-edge.webp", "Edge Control", "€8.99 excl. VAT · 12 or 24 units"],
    ["u18-huile-barbe.webp", "Beard oils", "€6.99 excl. VAT · 4 scents"],
    ["u23-masque-supreme.webp", "Masque Suprême", "€7.99 excl. VAT · 12 or 16 units"],
  ],
  steps: [
    [ShoppingCart, "Discover the products", "Browse the range on ozentya.org."],
    [UserPlus, "Contact us", "WhatsApp or email us with your business name, city, country and the products you want."],
    [FileText, "Receive your quote and order", "We confirm prices, quantities and shipping, then you place your order."],
  ],
  faq: [
    ["Who can become a reseller?", "Professionals: hair salons, independent stylists, barbershops, beauty and hair care stores, wholesalers and distributors."],
    ["What is the minimum order?", "Products are sold in fixed quantities (12, 16, 20, 24 units… depending on the product), starting from a single reference."],
    ["Do you ship outside France?", "Delivery is free in France. For the UK, Europe, Africa and other countries, contact us on WhatsApp or by email to confirm shipping conditions."],
    ["How do I get prices and a quote?", "Message us on WhatsApp (+33 6 25 99 55 02) or email pchaubo@hotmail.fr with your business details and the products you are interested in."],
    ["At what price can I resell?", "You set your own resale price."],
  ],
};

const Cta = ({ testid, light, t }) => (t.ctaTo === null ? (
  <a href={`${ORDER_WHATSAPP_URL}?text=${encodeURIComponent("Hello Ozentya, I am a professional and I would like information / to order. Business: … City / country: …")}`} target="_blank" rel="noreferrer" data-testid={testid}
    className={`group inline-flex items-center gap-2 rounded-full font-semibold px-7 py-4 min-h-[52px] transition-transform hover:-translate-y-0.5 ${light ? "bg-[var(--oz-gold)] text-[var(--oz-dark)]" : "bg-[var(--oz-dark)] text-white"}`}>
    {t.cta} <ArrowRight className="w-4 h-4" />
  </a>
) : (
  <Link to={t.ctaTo} data-testid={testid}
    className={`group inline-flex items-center gap-2 rounded-full font-semibold px-7 py-4 min-h-[52px] transition-transform hover:-translate-y-0.5 ${light ? "bg-[var(--oz-gold)] text-[var(--oz-dark)]" : "bg-[var(--oz-dark)] text-white"}`}>
    {t.cta} <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
  </Link>
));

export default function ResellerPage({ t = FR }) {
  useSeo(t.seo);
  return (
    <div data-testid="reseller-page">
      <section className="bg-[var(--oz-dark)] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 md:py-24 grid md:grid-cols-[1.3fr_1fr] gap-10 items-center">
          <div>
            <Link to={t.alt[1]} data-testid="reseller-lang-switch" className="inline-block mb-4 text-xs underline text-white/60 hover:text-white">{t.alt[0]}</Link>
            <p className="text-xs uppercase tracking-[0.2em] text-[var(--oz-gold)] font-semibold">{t.kicker}</p>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">{t.h1}</h1>
            <p className="mt-5 text-base md:text-lg text-white/75 max-w-xl">{t.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Cta testid="reseller-cta-top" light t={t} />
              <Link to={t.catalogTo} data-testid="reseller-catalog-link" className="inline-flex items-center rounded-full border border-white/30 px-7 py-4 min-h-[52px] font-semibold hover:border-[var(--oz-gold)] hover:text-[var(--oz-gold)] transition-colors">{t.catalog}</Link>
            </div>
            <OrderContact dark intl={t.intl} title={t.contactTop} className="mt-8" testid="reseller-order-contact-top" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {t.range.map(([img, name]) => (
              <div key={img} className="aspect-square rounded-2xl bg-white/95 p-2 overflow-hidden"><img src={`/img/pro/${img}`} alt={name} className="w-full h-full object-contain" loading="lazy" /></div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 md:py-20">
        <h2 className="font-serif text-3xl sm:text-4xl">{t.h2Perks}</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {t.perks.map(([Icon, t, d]) => (
            <div key={t} data-testid="reseller-perk" className="rounded-2xl border border-[var(--oz-border)] bg-white p-6 transition-transform hover:-translate-y-0.5">
              <Icon className="w-6 h-6 text-[var(--oz-gold)]" /><p className="mt-4 font-semibold">{t}</p><p className="mt-1 text-sm text-[var(--oz-muted)]">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-[var(--oz-bg-2,#f6f1ea)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 md:py-20">
          <h2 className="font-serif text-3xl sm:text-4xl">{t.h2Range}</h2>
          <p className="mt-3 text-sm text-[var(--oz-muted)] max-w-2xl">{t.rangeSub}</p>
          <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4">
            {t.range.map(([img, name, info]) => (
              <div key={name} data-testid="reseller-product" className="rounded-2xl bg-white border border-[var(--oz-border)] overflow-hidden">
                <div className="aspect-[4/3] bg-white p-4"><img src={`/img/pro/${img}`} alt={name} className="w-full h-full object-contain" loading="lazy" /></div>
                <div className="p-4 border-t border-[var(--oz-border)]"><p className="font-semibold text-sm">{name}</p><p className="text-xs text-[var(--oz-muted)] mt-1">{info}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-16 md:py-20">
        <h2 className="font-serif text-3xl sm:text-4xl">{t.h2Steps}</h2>
        <ol className="mt-10 grid md:grid-cols-3 gap-4">
          {t.steps.map(([Icon, t, d], i) => (
            <li key={t} className="rounded-2xl border border-[var(--oz-border)] bg-white p-6">
              <span className="text-xs font-semibold text-[var(--oz-gold)]">{t.step} {i + 1}</span>
              <Icon className="mt-3 w-6 h-6" /><p className="mt-3 font-semibold">{t}</p><p className="mt-1 text-sm text-[var(--oz-muted)]">{d}</p>
            </li>
          ))}
        </ol>
        <div className="mt-10"><Cta testid="reseller-cta-steps" t={t} /></div>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-8 pb-20">
        <h2 className="font-serif text-3xl sm:text-4xl">{t.h2Faq}</h2>
        <Accordion type="single" collapsible className="mt-8" data-testid="reseller-faq">
          {t.faq.map(([q, a], i) => (
            <AccordionItem key={q} value={`q${i}`}>
              <AccordionTrigger data-testid={`reseller-faq-${i}`} className="text-left">{q}</AccordionTrigger>
              <AccordionContent className="text-[var(--oz-muted)]">{a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <div className="mt-12 rounded-3xl bg-[var(--oz-dark)] text-white p-8 sm:p-10 border-2 border-[var(--oz-gold)]">
          <p className="font-serif text-2xl sm:text-3xl">{t.final}</p>
          <div className="mt-6"><Cta testid="reseller-cta-bottom" light t={t} /></div>
          <OrderContact dark intl={t.intl} title={t.contactBottom} className="mt-6" testid="reseller-order-contact-bottom" />
        </div>
      </section>
    </div>
  );
}
